import styled, { keyframes } from "styled-components";
import { T, FONTS, FS, BP } from "../tokens";

export const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: ${(p) => (p.$sm ? "30px" : "38px")};
  padding: 0 ${(p) => (p.$sm ? "10px" : "16px")};
  border-radius: 8px;
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${(p) => (p.$sm ? FS.sm : FS.md)};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 150ms,
    border-color 150ms,
    box-shadow 150ms,
    opacity 150ms;

  ${(p) =>
    p.$variant === "primary" &&
    `
      background: linear-gradient(135deg, ${T.teal} 0%, ${T.tealDark} 100%);
      border: 1px solid ${T.tealDark};
      color: ${T.white};
      box-shadow: 0 2px 8px rgba(0,131,140,0.28);
      &:hover:not(:disabled) {
        background: linear-gradient(135deg, ${T.tealDark} 0%, #044f55 100%);
        box-shadow: 0 4px 14px rgba(0,131,140,0.38);
      }
    `}

  ${(p) =>
    p.$variant === "ghost" &&
    `
      background: transparent;
      border: 1px solid transparent;
      color: ${T.mute};
      &:hover:not(:disabled) { background: ${T.line}; color: ${T.ink}; }
    `}

  ${(p) =>
    p.$variant === "outline" &&
    `
      background: ${T.white};
      border: 1.5px solid ${T.teal};
      color: ${T.teal};
      &:hover:not(:disabled) { background: ${T.tealTint}; }
    `}

  ${(p) =>
    !p.$variant &&
    `
      background: ${T.white};
      border: 1.5px solid ${T.line};
      color: ${T.ink};
      &:hover:not(:disabled) { background: ${T.soft}; border-color: ${T.subtle}; }
    `}

  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: ${BP.mobile}) {
    height: ${(p) => (p.$sm ? "28px" : "34px")};
    padding: 0 ${(p) => (p.$sm ? "8px" : "12px")};
  }
`;

export const IconBtn = styled.button`
  width: ${(p) => p.$size || "24px"};
  height: ${(p) => p.$size || "24px"};
  border-radius: 6px;
  border: 1px solid ${T.line};
  background: transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 120ms,
    border-color 120ms;

  &:hover:not(:disabled) {
    background: ${T.white};
    border-color: ${T.subtle};
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

export const PlainIconBtn = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 5px;
  border: none;
  background: transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 120ms;

  &:hover:not(:disabled) {
    background: ${T.navyTint};
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.xs};
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => p.$bg || T.line};
  color: ${(p) => p.$fg || T.ink};
  white-space: nowrap;
`;

export const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.$color || T.mute};
  flex-shrink: 0;
`;

export const CheckToggle = styled.button`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid ${(p) => (p.$on ? T.teal : T.line)};
  background: ${(p) => (p.$on ? T.teal : T.white)};
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 150ms;

  &:hover {
    border-color: ${T.teal};
  }
`;

export const TextInput = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1.5px solid ${T.line};
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.md};
  color: ${T.ink};
  background: ${T.white};
  box-sizing: border-box;
  outline: none;
  transition:
    border-color 150ms,
    box-shadow 150ms;

  &::placeholder {
    color: ${T.subtle};
  }
  &:focus {
    border-color: ${T.teal};
    box-shadow: 0 0 0 3px ${T.tealTint};
  }

  @media (max-width: ${BP.mobile}) {
    height: 38px;
    font-size: ${FS.base};
  }
`;

export const SelectInput = styled.select`
  width: 100%;
  height: 42px;
  padding: 0 36px 0 14px;
  border-radius: 8px;
  border: 1.5px solid ${T.line};
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.md};
  color: ${T.ink};
  background: ${T.white};
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%23949ab0' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  box-sizing: border-box;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition:
    border-color 150ms,
    box-shadow 150ms;

  &:focus {
    border-color: ${T.teal};
    box-shadow: 0 0 0 3px ${T.tealTint};
  }

  @media (max-width: ${BP.mobile}) {
    height: 38px;
  }
`;

export const SmallSelect = styled.select`
  height: 28px;
  padding: 0 28px 0 8px;
  border-radius: 6px;
  border: 1px solid ${T.line};
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.base};
  color: ${T.ink};
  background: ${T.white};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23949ab0' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: ${T.teal};
  }
`;

export const SearchWrap = styled.div`
  position: relative;
  flex: 1 1 180px;
  min-width: 140px;
`;

export const SearchIco = styled.span`
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  pointer-events: none;
`;

export const SearchBox = styled.input`
  width: 100%;
  height: 34px;
  padding: 0 10px 0 32px;
  border-radius: 8px;
  border: 1.5px solid ${T.line};
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.base};
  color: ${T.ink};
  background: ${T.white};
  box-sizing: border-box;
  outline: none;
  transition:
    border-color 150ms,
    box-shadow 150ms;

  &::placeholder {
    color: ${T.subtle};
  }
  &:focus {
    border-color: ${T.teal};
    box-shadow: 0 0 0 3px ${T.tealTint};
  }
`;

export const SourceChipBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.sm};
  font-weight: 500;
  border: 1.5px solid ${(p) => (p.$on ? p.$borderColor || T.teal : T.line)};
  background: ${(p) => (p.$on ? p.$bg || T.tealTint : T.white)};
  color: ${(p) => (p.$on ? p.$fg || T.tealDark : T.mute)};
  cursor: pointer;
  transition: all 150ms;

  &:hover {
    border-color: ${(p) => p.$borderColor || T.teal};
    color: ${(p) => p.$fg || T.tealDark};
  }
`;

export const PageStamp = styled.span`
  font-family: ${FONTS.mono};
  font-size: ${FS.xs};
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${T.navyTint};
  color: ${T.navy};
  flex-shrink: 0;
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
export const Spinner = styled.div`
  width: ${(p) => p.$size || "32px"};
  height: ${(p) => p.$size || "32px"};
  border: 3px solid ${T.tealTint};
  border-top-color: ${T.teal};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(14px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;
export const Toast = styled.div`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: ${T.navy};
  color: ${T.white};
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.base};
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  box-shadow: 0 10px 32px rgba(41, 52, 97, 0.3);
  z-index: 9999;
  animation: ${fadeUp} 0.22s ease;
  display: flex;
  align-items: center;
  gap: 8px;
`;
